import React from 'react';
import { useDashboard } from './DashboardContext';
import { GraduationCap, Play } from 'lucide-react';

const CoursesPage = () => {
  const {
    courses,
    enrollingCourseId,
    isEnrolled,
    handleEnrollCourse,
    openCourseViewer
  } = useDashboard();

  const enrolledCourses = courses.filter(c => isEnrolled(c.id));
  const availableCourses = courses.filter(c => !isEnrolled(c.id));

  return (
    <div className="page-content">
      <h1 className="page-title">Courses</h1>
      <p className="page-description">Learn and grow with our trading courses</p>
      
      {/* My Enrolled Courses */}
      {enrolledCourses.length > 0 && (
        <div className="courses-section">
          <h2 className="courses-section-title">My Courses</h2>
          <div className="courses-grid">
            {enrolledCourses.map(course => {
              const totalEpisodes = course.sections?.reduce((acc, s) => acc + (s.episodes?.length || 0), 0) || 0;
              const completedCount = course.sections?.reduce((acc, s) => {
                const savedProgress = localStorage.getItem(`course_progress_${course.id}`);
                const completed = savedProgress ? JSON.parse(savedProgress) : [];
                return acc + (s.episodes?.filter(e => completed.includes(e.id)).length || 0);
              }, 0) || 0;
              const progress = totalEpisodes > 0 ? Math.round((completedCount / totalEpisodes) * 100) : 0;
              
              return (
                <div key={course.id} className="course-card enrolled">
                  <div className="course-card-header">
                    {course.thumbnail_url ? (
                      <img src={course.thumbnail_url} alt={course.title} className="course-thumbnail" />
                    ) : (
                      <GraduationCap size={32} className="course-icon" />
                    )}
                    <span className="enrolled-badge">Enrolled</span>
                  </div>
                  <div className="course-card-body">
                    <h3 className="course-title">{course.title || 'Untitled Course'}</h3>
                    <p className="course-description">{course.description || 'No description available'}</p>
                    <div className="course-progress">
                      <div className="progress-bar-small">
                        <div className="progress-fill" style={{ width: `${progress}%` }} />
                      </div>
                      <span className="progress-text">{progress}% Complete</span>
                    </div>
                    <div className="course-stats">
                      <span>{course.sections?.length || 0} sections</span>
                      <span>{totalEpisodes} lessons</span>
                    </div>
                    <button 
                      className="continue-btn"
                      onClick={() => openCourseViewer(course.id)}
                    >
                      <Play size={16} />
                      Continue Learning
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* Available Courses */}
      {availableCourses.length > 0 && (
        <div className="courses-section">
          <h2 className="courses-section-title">
            {enrolledCourses.length > 0 ? 'More Courses' : 'Available Courses'}
          </h2>
          <div className="courses-grid">
            {availableCourses.map(course => (
              <div key={course.id} className="course-card">
                <div className="course-card-header">
                  {course.thumbnail_url ? (
                    <img src={course.thumbnail_url} alt={course.title} className="course-thumbnail" />
                  ) : (
                    <GraduationCap size={32} className="course-icon" />
                  )}
                </div>
                <div className="course-card-body">
                  <h3 className="course-title">{course.title || 'Untitled Course'}</h3>
                  <p className="course-description">{course.description || 'No description available'}</p>
                  <div className="course-meta-info">
                    <span className="course-instructor">{course.instructor ? `By ${course.instructor}` : ''}</span>
                    <span className="course-price">
                      {course.price > 0 ? `$${course.price}` : 'Free'}
                    </span>
                  </div>
                  <div className="course-stats">
                    <span>{course.sections?.length || 0} sections</span>
                    <span>{course.sections?.reduce((acc, s) => acc + (s.episodes?.length || 0), 0) || 0} lessons</span>
                  </div>
                  <button 
                    className="enroll-btn"
                    onClick={() => handleEnrollCourse(course.id)}
                    disabled={enrollingCourseId === course.id}
                  >
                    {enrollingCourseId === course.id ? (
                      'Processing...'
                    ) : course.price > 0 ? (
                      `Enroll Now - $${course.price}`
                    ) : (
                      'Enroll Free'
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {courses.length === 0 && (
        <div className="courses-empty-card">
          <div className="empty-state-large">
            <GraduationCap size={64} className="empty-icon" />
            <h3 className="empty-title">No courses available yet</h3>
            <p className="empty-message">Check back soon for new courses</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoursesPage;